import { Button } from "@mui/material";
import { AxiosResponse } from "axios";
import { Formik } from "formik";
import { omit } from "lodash-es";
import { forwardRef } from "react";
import * as Yup from "yup";

import FormContentBlock from "@/App/Layouts/FormContentBlock";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useEntityInputFormInfo } from "@/common/hooks/entity/useEntityInputFormInfo";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useCurrentCurrency } from "@/common/hooks/useCurrentCurrency";
import { RepairSpecDtoAll, RepairSpecDtoAny } from "@/common/mapping/mappers/repairSpec";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  CreateRepairSpecDto,
  EntityType,
  GeneralTaxDto,
  RepairMaterialDto,
  RepairMaterialFullReferenceDto,
  RepairSpecDetalizationType,
  RepairSpecDto,
  RepairSpecItemDto,
  RepairSpecItemInputDto,
  RepairSpecSparePartDto,
  RepairSpecSparePartInputDto,
  RepairWorkDto,
  RepairWorkFullReferenceDto,
  UpdateRepairSpecDto,
  VehicleType,
} from "@/core/api/generated/v1";

import FormActions from "../../../Form/FormActions";
import BaseEntityCreateUpdate, {
  BaseEntityCreateUpdateInheritableProps,
  FormWrapperHandle,
} from "../../components/BaseEntityCreateUpdate";
import { BaseEntityCreateUpdateExtras } from "../../components/BaseEntityCreateUpdateExtras";
import RepairSpecDataInputDtoFormInputs from "./RepairSpecDataInputDtoFormInputs";

type DefaultValues = {
  name?: string;
  partTypeId?: string;
  damageTypeId?: string;
  data?: RepairSpecDtoAny;
  dataAll?: RepairSpecDtoAll;
  currency?: CreateRepairSpecDto["currency"];
};

export type RepairSpecCreateUpdateHandle = FormWrapperHandle;

export interface RepairSpecCreateUpdateProps extends BaseEntityCreateUpdateInheritableProps<
  RepairSpecDto,
  DefaultValues
> {
  repairSpecId?: string;
  createFunc?: (params: {
    dto: CreateRepairSpecDto;
  }) => Promise<AxiosResponse<RepairSpecDto, unknown>>;
  updateFunc?: (params: {
    repairSpecId: string;
    dto: UpdateRepairSpecDto;
  }) => Promise<AxiosResponse<RepairSpecDto, unknown>>;
}

export default forwardRef<RepairSpecCreateUpdateHandle, RepairSpecCreateUpdateProps>(
  function RepairSpecCreateUpdate(
    {
      repairSpecId,
      defaultValues,
      onCreate,
      onUpdate,
      onSave,
      createFunc,
      updateFunc,
    }: RepairSpecCreateUpdateProps,
    ref,
  ) {
    defaultValues = defaultValues
      ? {
          ...defaultValues,
          dataAll: defaultValues.data ? (defaultValues.data as RepairSpecDtoAll) : undefined,
        }
      : undefined;

    const mounted = useMounted();
    const { enqueueSnackbar } = useAppSnackbar();
    const currentCurrency = useCurrentCurrency();
    const formInfo = useEntityInputFormInfo({ entityId: repairSpecId });

    const repairSpecRequest = useApiRequest(
      apiClient.repairSpecsApi.apiV1RepairSpecsRepairSpecIdGet,
      {
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        repairSpecId: repairSpecId!,
      },
      {
        skip: !repairSpecId,
      },
    );
    const repairSpec = repairSpecRequest?.data;

    useBreadcrumbReplacements({
      waitTimeout: 10_000,
      idBreadcrumb: repairSpec && {
        idValue: repairSpec.id!,
        newTitle: repairSpec.localNumber || "",
      },
    });

    return (
      <BaseEntityCreateUpdate
        entityType={EntityType.RepairSpec}
        entityId={repairSpecId}
        entity={repairSpec}
        entityRequest={repairSpecRequest}
        isIniting={!currentCurrency}
      >
        <Formik<
          Omit<CreateRepairSpecDto, "items"> &
            Omit<UpdateRepairSpecDto, "items"> & {
              items?: Array<
                RepairSpecItemInputDto & {
                  sourceType?: RepairSpecItemDto["sourceType"];
                  material?: RepairMaterialDto | RepairMaterialFullReferenceDto;
                  work?: RepairWorkDto | RepairWorkFullReferenceDto;
                  tax?: GeneralTaxDto | null;
                  sparePart?: RepairSpecSparePartDto & RepairSpecSparePartInputDto;
                  isSpotFactorChanged?: boolean;
                }
              >;
              submit?: string | null;
            }
        >
          enableReinitialize={false}
          initialValues={{
            sourceTenantId: formInfo.isCreate ? defaultValues?.dataAll?.tenantId : undefined,
            sourceSpecId: formInfo.isCreate ? defaultValues?.dataAll?.id : undefined,
            currency:
              repairSpec?.currency ||
              defaultValues?.currency ||
              defaultValues?.dataAll?.currency ||
              currentCurrency ||
              undefined,
            vehicleType:
              repairSpec?.vehicleType || defaultValues?.dataAll?.vehicleType || VehicleType.Car,
            damageTypeId:
              repairSpec?.damageType?.id ||
              defaultValues?.damageTypeId ||
              defaultValues?.dataAll?.damageTypeId ||
              defaultValues?.dataAll?.damageType?.id ||
              undefined,
            partTypeId:
              repairSpec?.partType?.id ||
              defaultValues?.partTypeId ||
              defaultValues?.dataAll?.partTypeId ||
              defaultValues?.dataAll?.partType?.id ||
              undefined,
            name:
              repairSpec?.name || defaultValues?.name || defaultValues?.dataAll?.name || undefined,
            description:
              repairSpec?.description || defaultValues?.dataAll?.description || undefined,
            spotFactor: repairSpec?.spotFactor || defaultValues?.dataAll?.spotFactor || 1,
            detalization: {
              ...repairSpec?.detalization,
              type:
                repairSpec?.detalization?.type ||
                defaultValues?.dataAll?.detalization?.type ||
                RepairSpecDetalizationType.General,
              vehicleSize:
                repairSpec?.detalization?.vehicleSize ||
                defaultValues?.dataAll?.detalization?.vehicleSize ||
                undefined,
              bodyType:
                repairSpec?.detalization?.bodyType ||
                defaultValues?.dataAll?.detalization?.bodyType ||
                undefined,
              fuelType:
                repairSpec?.detalization?.fuelType ||
                defaultValues?.dataAll?.detalization?.fuelType ||
                undefined,
              makeId:
                repairSpec?.detalization?.make?.id ||
                defaultValues?.dataAll?.detalization?.makeId ||
                defaultValues?.dataAll?.detalization?.make?.id ||
                undefined,
              modelId:
                repairSpec?.detalization?.model?.id ||
                defaultValues?.dataAll?.detalization?.modelId ||
                defaultValues?.dataAll?.detalization?.model?.id ||
                undefined,
              generationId:
                repairSpec?.detalization?.generation?.id ||
                defaultValues?.dataAll?.detalization?.generationId ||
                defaultValues?.dataAll?.detalization?.generation?.id ||
                undefined,
              modificationId:
                repairSpec?.detalization?.modification?.id ||
                defaultValues?.dataAll?.detalization?.modificationId ||
                defaultValues?.dataAll?.detalization?.modification?.id ||
                undefined,
            },
            items:
              (repairSpec?.items || defaultValues?.dataAll?.items)?.map((x) => ({
                ...x,
                materialId: x.material?.id,
                workId: x.work?.id,
                amount: x?.amount ?? 1,
                spotFactor: x?.spotFactor ?? 1,
                material: x.material || undefined,
                work: x.work || undefined,
                sparePart:
                  (x.sparePart && {
                    ...x.sparePart,
                  }) ||
                  undefined,
                custom: x.custom || undefined,
                notes: x.notes || undefined,
              })) || undefined,
            submit: "",
          }}
          validationSchema={Yup.object().shape({
            // name: Yup.string().required("This field is required"),
          })}
          onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
            try {
              if (formInfo.isCreate) {
                const response = createFunc
                  ? await createFunc({
                      dto: {
                        ...omit(values, "submit"),
                      },
                    })
                  : await apiClient.repairSpecsApi.apiV1RepairSpecsPost({
                      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                      createRepairSpecDto: {
                        ...omit(values, "submit"),
                      },
                    });
                enqueueSnackbar("Repair spec created.", { variant: "success" });
                onCreate && onCreate(response.data);
                onSave && onSave(response.data);
              } else {
                const response = updateFunc
                  ? await updateFunc({
                      repairSpecId: repairSpecId || "",
                      dto: {
                        ...omit(values, "submit"),
                      },
                    })
                  : await apiClient.repairSpecsApi.apiV1RepairSpecsRepairSpecIdPut({
                      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                      repairSpecId: repairSpecId || "",
                      updateRepairSpecDto: {
                        ...omit(values, "submit"),
                      },
                    });
                enqueueSnackbar("Repair spec updated.", { variant: "success" });
                onUpdate && onUpdate(response.data);
                onSave && onSave(response.data);
              }

              if (mounted.current) {
                setStatus({ success: true });
                setSubmitting(false);
              }
            } catch (err: any) {
              if (mounted.current) {
                ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
                setStatus({ success: false });
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {(formikProps) => {
            const { handleSubmit, isSubmitting, values, setValues } = formikProps;

            return (
              <form noValidate onSubmit={handleSubmit}>
                <BaseEntityCreateUpdateExtras
                  defaultImperativeHandle={{
                    formWrapperRef: ref,
                    formikProps: formikProps,
                  }}
                  guardProps={{
                    formikProps: formikProps,
                  }}
                />

                <FormContentBlock>
                  <RepairSpecDataInputDtoFormInputs
                    mode={(formInfo.isUpdate && "update") || "create"}
                    repairSpecId={repairSpecId}
                    values={values || {}}
                    // formikProps={{
                    //   errors: errors,
                    //   touched: touched,
                    // }}
                    formikProps={formikProps}
                    withGeneralValidationError
                    onChange={(newValue) => setValues({ ...newValue })}
                  />
                </FormContentBlock>

                <FormActions>
                  <Button
                    color='primary'
                    loading={isSubmitting}
                    fullWidth
                    type='submit'
                    variant='contained'
                  >
                    Save
                  </Button>
                </FormActions>
              </form>
            );
          }}
        </Formik>
      </BaseEntityCreateUpdate>
    );
  },
);
